import {endOfDay, isAfter} from 'date-fns';

import type {
  AfriexCurrencyCode,
  CreateBusinessInvoicePayload,
  UpdateBusinessInvoiceStatusPayload,
} from '@/types';
import {BusinessInvoiceStatus, BusinessInvoiceType} from '@/types';

type InvoiceDraftInput = {
  businessId?: string;
  description?: string;
  amount?: number | '';
  currency?: AfriexCurrencyCode | '';
  periodStart?: Date;
  periodEnd?: Date;
  dueDate?: Date;
  settlementAccountId?: string;
};

// A function declaration, not a const arrow: TypeScript only narrows control
// flow through a never-returning call when it is declared this way.
function throwErrorMessage(errorMessage: string): never {
  throw new Error(errorMessage);
}

export class InvoiceValidator {
  constructor(private request: InvoiceDraftInput) {}

  /**
   * Checks run inline rather than in separate methods so TypeScript narrows
   * each value as it is proved present: `throwErrorMessage` returns `never`,
   * so everything past a check is known non-null without an assertion.
   *
   * Validation stops at the first failure, matching
   * `BusinessPoolAccountValidator` and the rest of the validators here.
   */
  value(): CreateBusinessInvoicePayload {
    const {
      businessId,
      amount,
      currency,
      periodStart,
      periodEnd,
      dueDate,
      settlementAccountId,
      description,
    } = this.request;

    if (!businessId?.trim()) {
      throwErrorMessage('No business selected');
    }

    const numericAmount = Number(amount);
    if (!amount || Number.isNaN(numericAmount)) {
      throwErrorMessage('No amount provided');
    }
    if (numericAmount <= 0) {
      throwErrorMessage('Amount must be greater than zero');
    }

    if (!currency) {
      throwErrorMessage('No currency selected');
    }

    if (!periodStart) {
      throwErrorMessage('No period start provided');
    }
    if (!periodEnd) {
      throwErrorMessage('No period end provided');
    }
    // An invoice bills a period that has already happened, so neither end of
    // it may fall after today. The due date is exempt: it is normally ahead.
    const endOfToday = endOfDay(new Date());
    if (isAfter(periodStart, endOfToday)) {
      throwErrorMessage('Period start cannot be in the future');
    }
    if (isAfter(periodEnd, endOfToday)) {
      throwErrorMessage('Period end cannot be in the future');
    }
    if (periodEnd <= periodStart) {
      throwErrorMessage('Period end must be after period start');
    }

    if (!dueDate) {
      throwErrorMessage('No payment due date provided');
    }
    // An invoice due before the period it bills has closed cannot be settled in
    // full, so the due date is anchored to the period end, not to today.
    if (dueDate < periodEnd) {
      throwErrorMessage('Due date must be on or after the period end');
    }

    if (!settlementAccountId) {
      throwErrorMessage('No settlement account selected');
    }

    const trimmedDescription = description?.trim();

    return {
      toUser: {userId: businessId.trim(), userType: 'BUSINESS'},
      // Not a form field: this screen only ever raises fee invoices.
      invoiceType: BusinessInvoiceType.BusinessFeeInvoice,
      amount: numericAmount,
      currency,
      periodStart: periodStart.toISOString(),
      periodEnd: periodEnd.toISOString(),
      dueDate: dueDate.toISOString(),
      settlementAccountId,
      // Omitted entirely rather than sent blank.
      ...(trimmedDescription ? {description: trimmedDescription} : {}),
    };
  }
}

type StatusDraftInput = {
  status?: BusinessInvoiceStatus;
  amountPaid?: number | '';
  note?: string;
};

export class InvoiceStatusValidator {
  constructor(
    private request: StatusDraftInput,
    private invoiceAmount: number,
  ) {}

  value(): UpdateBusinessInvoiceStatusPayload {
    const {status, amountPaid, note} = this.request;

    if (!status) {
      throwErrorMessage('No status selected');
    }

    const trimmedNote = note?.trim();

    // Only a part payment needs an amount: every other status is a statement
    // about the invoice, not about money received.
    if (status !== BusinessInvoiceStatus.PartiallyPaid) {
      return {
        status,
        ...(trimmedNote ? {note: trimmedNote} : {}),
      };
    }

    const numericAmountPaid = Number(amountPaid);
    if (!amountPaid || Number.isNaN(numericAmountPaid)) {
      throwErrorMessage('Enter the amount that was paid');
    }
    if (numericAmountPaid <= 0) {
      throwErrorMessage('Amount paid must be greater than zero');
    }
    if (numericAmountPaid >= this.invoiceAmount) {
      throwErrorMessage(
        'A part payment must be less than the invoice amount; mark it paid instead',
      );
    }

    return {
      status,
      amountPaid: numericAmountPaid,
      ...(trimmedNote ? {note: trimmedNote} : {}),
    };
  }
}
